import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useSubmissionTemplateVersion,
  SubmissionTemplateVersionData,
} from '@/hooks/submission_forms/useSubmissionTemplateVersion';
import { useResetToProdButton } from '@/hooks/submission_forms/useResetToProdButton';
import ResetToProdButton from './ResetToProdButton';
import { StatusBadge } from '@/components/ui/StatusBadge';
import {
  DiffSection,
  ComparisonResult,
  diffTotal,
} from './components/VersionDiff';
import ReviewChangesDialog from './components/ReviewChangesDialog';

interface HistoryPanelProps {
  templateId: string;
  onDraftOpened?: () => void;
  // Manual template editing is off (templates are managed in code): hide
  // every action that writes — open draft, revert, reset to prod — and keep
  // the version table and Compare.
  readOnly?: boolean;
}

// Tab content for the "History" tab of the template workspace — this is the
// former standalone Versions.tsx page body, unchanged internally beyond:
// (1) losing its own page chrome (breadcrumb/header now live in the shared
// Workspace shell), (2) "Continue editing"/"Create new draft" now switches
// tabs instead of navigating to a separate route, (3) the base/target
// version selection is mirrored into the URL query string (?base=&target=)
// so a specific comparison is bookmarkable/shareable.
const HistoryPanel: React.FC<HistoryPanelProps> = ({
  templateId,
  onDraftOpened,
  readOnly = false,
}) => {
  const {
    listVersions,
    revertToVersion,
    compareVersions,
    openOrLoadDraft,
    getResetStatus,
    triggerReset,
  } = useSubmissionTemplateVersion();

  const [searchParams, setSearchParams] = useSearchParams();

  const [versions, setVersions] = useState<SubmissionTemplateVersionData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isOpeningDraft, setIsOpeningDraft] = useState(false);

  const [baseVersionId, setBaseVersionId] = useState(
    searchParams.get('base') || ''
  );
  const [targetVersionId, setTargetVersionId] = useState(
    searchParams.get('target') || ''
  );
  const [comparison, setComparison] = useState<ComparisonResult | null>(null);
  const [isComparing, setIsComparing] = useState(false);
  const [revertTarget, setRevertTarget] =
    useState<SubmissionTemplateVersionData | null>(null);
  // version id -> [changes, comparedToVersionNumber], so the table reads at
  // a glance without opening Compare. Published/archived rows compare
  // against their immediate numeric predecessor (what actually changed at
  // that publish). The draft row instead compares against whatever's
  // currently published — its real baseline — since after a revert its
  // numeric predecessor may not be what it was actually opened from.
  const [changeCounts, setChangeCounts] = useState<
    Record<string, { count: number; comparedTo: number }>
  >({});
  // Mirrors changeCounts for synchronous reads inside load() below, without
  // making load() depend on (and re-create every time) changeCounts itself.
  const changeCountsRef = useRef(changeCounts);
  changeCountsRef.current = changeCounts;

  const getStatusForId = useCallback(
    () => getResetStatus(templateId),
    [templateId, getResetStatus]
  );
  const triggerForId = useCallback(
    () => triggerReset(templateId),
    [templateId, triggerReset]
  );
  const {
    status: resetStatus,
    isInFlight: isResetInFlight,
    notice: resetNotice,
    error: resetError,
    handleTrigger: handleResetToProd,
  } = useResetToProdButton({
    getStatus: getStatusForId,
    trigger: triggerForId,
    confirmMessage:
      "Reset this template to prod's currently published version? This publishes a new local version copying prod's tree.",
    startedMessage:
      'Reset to prod started — refresh in a moment to see the new published version.',
    failedMessage: 'Failed to start reset to prod',
  });

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listVersions(templateId);
      setVersions(data);
      const publishedVersion = data.find(v => v.status === 'published');
      // Versions come back newest-first. Published/archived rows compare
      // against their numeric predecessor (next array entry) — a stable
      // pairing that never changes once a version exists, so only fetch
      // pairs not already cached. The draft row compares against whatever's
      // currently published instead of its numeric predecessor, since after
      // a revert those can differ (see DiscardDraftService / RevertService).
      const pairs = data
        .slice(0, -1)
        .map((v, i) => {
          const previous =
            v.status === 'draft' ? publishedVersion : data[i + 1];
          return previous ? ([v, previous] as const) : null;
        })
        .filter(
          (
            pair
          ): pair is readonly [
            SubmissionTemplateVersionData,
            SubmissionTemplateVersionData,
          ] => pair !== null
        )
        .filter(([current, previous]) => {
          const cached = changeCountsRef.current[current.id];
          // Every non-draft pairing is permanent once it exists, so a cache
          // hit is always still correct. The draft's comparison target can
          // change (revert, publish) even though its own id doesn't, so its
          // cache entry is only valid while it still points at the same
          // predecessor version.
          return !cached || cached.comparedTo !== previous.version_number;
        });
      if (pairs.length > 0) {
        const results = await Promise.allSettled(
          pairs.map(([current, previous]) =>
            compareVersions(previous.id, current.id)
          )
        );
        const newCounts: Record<string, { count: number; comparedTo: number }> =
          {};
        results.forEach((result, i) => {
          if (result.status === 'fulfilled') {
            const [current, previous] = pairs[i];
            newCounts[current.id] = {
              count: diffTotal(result.value),
              comparedTo: previous.version_number,
            };
          }
        });
        setChangeCounts(current => ({ ...current, ...newCounts }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    } finally {
      setIsLoading(false);
    }
  }, [templateId, listVersions, compareVersions]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (resetNotice) setNotice(resetNotice);
  }, [resetNotice]);

  useEffect(() => {
    if (resetError) setError(resetError);
  }, [resetError]);

  const runCompare = useCallback(
    async (base: string, target: string) => {
      setIsComparing(true);
      setError(null);
      setComparison(null);
      try {
        const result = await compareVersions(base, target);
        setComparison(result);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to compare versions'
        );
      } finally {
        setIsComparing(false);
      }
    },
    [compareVersions]
  );

  // Restore a bookmarked or shared comparison link (?base=&target=) once,
  // on the initial load. Guarded by a ref rather than depending on
  // `isLoading` directly — load() also runs after a revert, and without
  // this guard that would silently re-trigger this same comparison again
  // (explicit Compare clicks below already handle re-running on demand).
  const hasRestoredComparisonFromUrl = useRef(false);
  useEffect(() => {
    if (isLoading || hasRestoredComparisonFromUrl.current) return;
    hasRestoredComparisonFromUrl.current = true;
    const base = searchParams.get('base');
    const target = searchParams.get('target');
    if (base && target) {
      runCompare(base, target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const draft = versions.find(v => v.status === 'draft');
  const published = versions.find(v => v.status === 'published');

  // openOrLoadDraft is find-or-create: safe to call whether or not a draft
  // already exists, so this one button covers both "start a new draft" and
  // "continue the existing one".
  const handleOpenDraft = async () => {
    setIsOpeningDraft(true);
    setError(null);
    try {
      await openOrLoadDraft(templateId);
      onDraftOpened?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open draft');
    } finally {
      setIsOpeningDraft(false);
    }
  };

  const handleConfirmRevert = async () => {
    if (!revertTarget) return;
    setError(null);
    try {
      const reverted = await revertToVersion(templateId, revertTarget.id);
      setNotice(`Reverted — published as version ${reverted.version_number}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revert');
      throw err;
    }
  };

  const handleCompare = async () => {
    if (!baseVersionId || !targetVersionId) return;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('base', baseVersionId);
        next.set('target', targetVersionId);
        return next;
      },
      { replace: true }
    );
    await runCompare(baseVersionId, targetVersionId);
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-gray-500 font-sans text-sm">
        Loading versions…
      </div>
    );
  }

  return (
    <div className="flex flex-col font-sans text-sm">
      {!readOnly && (
        <div className="px-5 py-3 flex items-center justify-end shrink-0">
          <ResetToProdButton
            status={resetStatus}
            isInFlight={isResetInFlight}
            onClick={handleResetToProd}
            label="Reset to Prod"
            inProgressLabel="Reset in progress…"
            title="Sync this template to whatever's currently published in prod"
          />
        </div>
      )}

      {(notice || error) && (
        <div
          className={`px-5 py-2 text-xs border-b ${error ? 'text-red-700 bg-red-50' : 'text-green-700 bg-green-50'}`}
        >
          {error || notice}
        </div>
      )}

      <div className="p-5 space-y-6">
        {!readOnly && (
          <div className="bg-white border rounded p-4 flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-700">
                {draft
                  ? `Draft in progress — v${draft.version_number}`
                  : 'No draft in progress'}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {draft
                  ? 'Continue editing the current draft, or publish it from the editor.'
                  : published
                    ? `Opens a new draft copied from the published v${published.version_number}.`
                    : 'Opens a new, empty draft to start building this template.'}
              </div>
            </div>
            <button
              onClick={handleOpenDraft}
              disabled={isOpeningDraft}
              className="text-xs bg-gray-900 text-white rounded px-3 py-1.5 hover:bg-gray-700 disabled:opacity-40 shrink-0"
            >
              {isOpeningDraft
                ? 'Opening…'
                : draft
                  ? 'Continue editing draft'
                  : 'Create new draft'}
            </button>
          </div>
        )}

        <table className="w-full bg-white text-xs border rounded overflow-hidden">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-2 text-gray-500 font-medium">
                Version
              </th>
              <th className="text-left px-4 py-2 text-gray-500 font-medium">
                Status
              </th>
              <th className="text-left px-4 py-2 text-gray-500 font-medium">
                Published
              </th>
              <th className="text-left px-4 py-2 text-gray-500 font-medium">
                Changes
              </th>
              <th className="px-4 py-2 w-40"></th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v, i) => {
              const revertedFrom = v.reverted_from_version_id
                ? versions.find(
                    candidate => candidate.id === v.reverted_from_version_id
                  )
                : null;
              return (
                <tr key={v.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-mono">
                    v{v.version_number}
                    {revertedFrom && (
                      <span className="ml-1.5 font-sans font-normal text-purple-700 bg-purple-50 rounded px-1.5 py-0.5 text-[11px] whitespace-nowrap">
                        reverted from v{revertedFrom.version_number}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={v.status} variant="entity" />
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">
                    {v.published_at
                      ? new Date(v.published_at).toLocaleString()
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">
                    {i === versions.length - 1 ? (
                      'First version'
                    ) : v.id in changeCounts ? (
                      <>
                        {changeCounts[v.id].count}{' '}
                        {v.status === 'draft' ? (
                          <span title="Compared to the currently published version, not the row below — this draft's baseline can differ from its numeric predecessor after a revert.">
                            vs live v{changeCounts[v.id].comparedTo}
                          </span>
                        ) : (
                          `vs v${changeCounts[v.id].comparedTo}`
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {!readOnly &&
                      v.status !== 'published' &&
                      v.status !== 'draft' && (
                        <button
                          onClick={() => setRevertTarget(v)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          Review & revert
                        </button>
                      )}
                  </td>
                </tr>
              );
            })}
            {versions.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                  No versions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="bg-white border rounded p-4">
          <div className="font-medium text-gray-700 mb-3">Compare versions</div>
          <div className="flex items-center gap-2 mb-3">
            <select
              value={baseVersionId}
              onChange={e => setBaseVersionId(e.target.value)}
              className="border rounded px-2 py-1.5 text-xs"
            >
              <option value="">Base version…</option>
              {versions.map(v => (
                <option key={v.id} value={v.id}>
                  v{v.version_number} ({v.status})
                </option>
              ))}
            </select>
            <span className="text-gray-400">vs</span>
            <select
              value={targetVersionId}
              onChange={e => setTargetVersionId(e.target.value)}
              className="border rounded px-2 py-1.5 text-xs"
            >
              <option value="">Target version…</option>
              {versions.map(v => (
                <option key={v.id} value={v.id}>
                  v{v.version_number} ({v.status})
                </option>
              ))}
            </select>
            <button
              onClick={handleCompare}
              disabled={!baseVersionId || !targetVersionId || isComparing}
              className="text-xs bg-gray-900 text-white rounded px-3 py-1.5 hover:bg-gray-700 disabled:opacity-40"
            >
              {isComparing ? 'Comparing…' : 'Compare'}
            </button>
          </div>

          {comparison && (
            <div className="space-y-4">
              <DiffSection
                title="Questions"
                side={comparison.questions}
                labelKey="prompt"
              />
              <DiffSection
                title="Groups"
                side={comparison.groups}
                labelKey="label"
              />
            </div>
          )}
        </div>
      </div>

      {revertTarget && (
        <ReviewChangesDialog
          open={!!revertTarget}
          onOpenChange={open => {
            if (!open) setRevertTarget(null);
          }}
          title={`Revert to v${revertTarget.version_number}`}
          description={
            published
              ? `Reverting publishes a new version copying v${revertTarget.version_number}'s tree. Here's how it differs from the currently live v${published.version_number}:`
              : `Reverting publishes a new version copying v${revertTarget.version_number}'s tree.`
          }
          confirmLabel="Revert"
          confirmingLabel="Reverting…"
          loadComparison={
            published
              ? () => compareVersions(published.id, revertTarget.id)
              : undefined
          }
          onConfirm={handleConfirmRevert}
        />
      )}
    </div>
  );
};

export default HistoryPanel;
